import React, { useState, useEffect } from "react";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { Link } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  RefreshCw,
  ArrowLeft,
  Download,
  AlertCircle,
  TrendingUp,
  Users,
  Briefcase,
  MapPin,
} from "lucide-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const API_URL = process.env.REACT_APP_API_URL || "https://sbg-backend.onrender.com";

// Skeleton loader component
const SkeletonLoader = () => (
  <div className="animate-pulse">
    <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-white p-6 rounded-lg shadow">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      ))}
    </div>
  </div>
);

// Stat card component
const StatCard = ({ title, value, icon: Icon, trend, color }) => (
  <div className="bg-white p-6 rounded-lg shadow">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center">
        <div className={`p-2 rounded-full ${color} bg-opacity-10`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
        <h3 className="text-lg font-semibold ml-3">{title}</h3>
      </div>
      {trend && (
        <div className={`text-sm ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </div>
      )}
    </div>
    <p className="text-3xl font-bold">{value}</p>
  </div>
);

const Analytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState("month");
  const [retryCount, setRetryCount] = useState(0);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/admin/analytics?range=${dateRange}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log("Received analytics data:", data);
      setAnalyticsData(data);
      setRetryCount(0);
    } catch (error) {
      console.error("Error fetching analytics data:", error);
      setError("Failed to load analytics data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    fetchAnalyticsData();
  };

  const handleExport = () => {
    if (!analyticsData) return;
    
    const dataStr = JSON.stringify(analyticsData, null, 2);
    const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
    
    const exportFileDefaultName = `analytics-${dateRange}-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  if (loading) return <SkeletonLoader />;
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Error Loading Analytics</h2>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={handleRetry}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  if (!analyticsData) return <div className="text-center p-4">No data available</div>;

  const { jobPostings, applications, userActivity } = analyticsData;

  const jobTypeData =
    jobPostings.byJobType && jobPostings.byJobType.length > 0
      ? jobPostings.byJobType.filter(
          (item) =>
            item.jobType != null &&
            item.jobType !== "null" &&
            item.jobType !== ""
        )
      : [{ jobType: "No data", count: jobPostings.total }];

  const jobTypeChartData = {
    labels: jobTypeData.map((item) => item.jobType),
    datasets: [
      {
        data: jobTypeData.map((item) => item.count),
        backgroundColor: [
          "#FF6384",
          "#36A2EB",
          "#FFCE56",
          "#4BC0C0",
          "#9966FF",
          "#FF9F40",
        ],
        borderWidth: 1,
      },
    ],
  };

  const topLocationsChartData = {
    labels:
      jobPostings.topLocations && jobPostings.topLocations.length > 0
        ? jobPostings.topLocations.map((item) => item.location)
        : ["No data"],
    datasets: [
      {
        label: "Job Count",
        data:
          jobPostings.topLocations && jobPostings.topLocations.length > 0
            ? jobPostings.topLocations.map((item) => item.count)
            : [jobPostings.total],
        backgroundColor: "#36A2EB",
        borderWidth: 1,
      },
    ],
  };

  const applicationsOverTimeChartData = {
    labels: applications.overTime.map((item) =>
      new Date(item.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    ),
    datasets: [
      {
        label: "Applications",
        data: applications.overTime.map((item) => item.count),
        fill: true,
        backgroundColor: "rgba(54, 162, 235, 0.1)",
        borderColor: "#36A2EB",
        tension: 0.4,
      },
    ],
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Link
            to="/admin-dashboard"
            className="flex items-center text-blue-500 hover:underline mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="p-2 border rounded bg-white"
          >
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="year">Last Year</option>
            <option value="all">All Time</option>
          </select>
          <button
            onClick={handleExport}
            className="p-2 bg-green-500 text-white rounded hover:bg-green-600"
            title="Export Data"
          >
            <Download className="w-5 h-5" />
          </button>
          <button
            onClick={fetchAnalyticsData}
            className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            title="Refresh Data"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="mb-6">
        <p className="text-gray-600">
          Date Range:{" "}
          <span className="font-bold">{analyticsData.dateRange.start}</span> to{" "}
          <span className="font-bold">{analyticsData.dateRange.end}</span>
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatCard
          title="Total Jobs"
          value={jobPostings.total}
          icon={Briefcase}
          color="text-blue-500"
        />
        <StatCard
          title="Active Jobs"
          value={jobPostings.active}
          icon={TrendingUp}
          color="text-green-500"
        />
        <StatCard
          title="Total Applications"
          value={applications.total}
          icon={Users}
          color="text-purple-500"
        />
        <StatCard
          title="Top Location"
          value={jobPostings.topLocations?.[0]?.location || "N/A"}
          icon={MapPin}
          color="text-red-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Job Postings</h2>
          <div className="flex justify-between mb-4">
            <div>
              <p className="text-gray-600">Total Jobs</p>
              <p className="text-2xl font-bold">{jobPostings.total}</p>
            </div>
            <div>
              <p className="text-gray-600">Active Jobs</p>
              <p className="text-2xl font-bold">{jobPostings.active}</p>
            </div>
          </div>
          <h3 className="text-lg font-semibold mb-2">Jobs by Type</h3>
          {jobTypeChartData.labels.length > 0 &&
          jobTypeChartData.datasets[0].data.some((count) => count > 0) ? (
            <Doughnut
              data={jobTypeChartData}
              options={{
                plugins: {
                  legend: { position: "right" },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const label = context.label || '';
                        const value = context.raw || 0;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = Math.round((value / total) * 100);
                        return `${label}: ${value} (${percentage}%)`;
                      }
                    }
                  }
                }
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-100 rounded">
              <p className="text-gray-500">No job type data available</p>
            </div>
          )}
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Top Locations</h2>
          {topLocationsChartData.labels.length > 0 &&
          topLocationsChartData.datasets[0].data.some((count) => count > 0) ? (
            <Bar
              data={topLocationsChartData}
              options={{
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Number of Jobs'
                    }
                  },
                  x: {
                    title: {
                      display: true,
                      text: 'Location'
                    }
                  }
                },
                plugins: {
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        return `Jobs: ${context.raw}`;
                      }
                    }
                  }
                }
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-100 rounded">
              <p className="text-gray-500">No location data available</p>
            </div>
          )}
        </div>

        <div className="bg-white p-6 rounded-lg shadow md:col-span-2">
          <h2 className="text-xl font-semibold mb-4">Applications Over Time</h2>
          <p className="text-gray-600 mb-4">
            Total Applications:{" "}
            <span className="font-bold">{applications.total}</span>
          </p>
          {applicationsOverTimeChartData.labels.length > 0 &&
          applicationsOverTimeChartData.datasets[0].data.some(
            (count) => count > 0
          ) ? (
            <div className="h-80">
              <Line
                data={applicationsOverTimeChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      title: { display: true, text: "Date" },
                      grid: {
                        display: false
                      }
                    },
                    y: {
                      beginAtZero: true,
                      title: { display: true, text: "Number of Applications" },
                      grid: {
                        color: "rgba(0, 0, 0, 0.1)"
                      }
                    },
                  },
                  plugins: {
                    legend: { display: true, position: "top" },
                    tooltip: {
                      mode: "index",
                      intersect: false,
                      callbacks: {
                        label: function(context) {
                          return `Applications: ${context.raw}`;
                        }
                      }
                    },
                  },
                  interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-100 rounded">
              <p className="text-gray-500">No application data available</p>
            </div>
          )}
        </div>
      </div>

      {userActivity && (
        <div className="mt-6 bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">User Activity</h2>
          <p className="text-gray-600 mb-4">
            New Users:{" "}
            <span className="font-bold">{userActivity.newUsers}</span>
          </p>
          {userActivity.overTime && userActivity.overTime.length > 0 ? (
            <div className="h-80">
              <Line
                data={{
                  labels: userActivity.overTime.map((item) =>
                    new Date(item.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })
                  ),
                  datasets: [
                    {
                      label: "New Users",
                      data: userActivity.overTime.map((item) => item.newUsers),
                      fill: false,
                      borderColor: "#4BC0C0",
                    },
                    {
                      label: "Active Users",
                      data: userActivity.overTime.map(
                        (item) => item.activeUsers
                      ),
                      fill: false,
                      borderColor: "#FFCE56",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: { title: { display: true, text: "Date" } },
                    y: {
                      beginAtZero: true,
                      title: { display: true, text: "Number of Users" },
                    },
                  },
                }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-100 rounded">
              <p className="text-gray-500">No user activity data available</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Analytics;
